import type { PoolPositions } from '@myrewards/contracts';
import type { ReactElement } from 'react';
import {
  ButtonLink,
  Card,
  DataTable,
  EmptyState,
  formatMoney,
  StatusPill,
  type DataColumn,
} from '../ui';
import { poolsInOrder } from './model';
import s from './Home.module.css';

// APP-148 R82 (the Home board): a Stock Admin's Budget pools, below Waiting on others. Each pool's
// cash for the year, read from Budget pools (`GET /api/v1/budgets`, Home's own read), in Home's
// pool order. A pool its awards would take below zero says so in words, never by colour alone.

type Pool = PoolPositions['pools'][number];

/** Below zero once what's in progress is paid: an award waits on a top-up. */
const overIfApproved = (p: Pool): boolean => p.remaining !== null && p.remaining.cashCents < 0;

function columns(year: string): DataColumn<Pool>[] {
  return [
    {
      id: 'pool',
      header: 'Pool',
      card: 'title',
      cell: (p) => (
        <span className={s.poolNameCell}>
          {p.pool.name}
          {overIfApproved(p) && (
            <StatusPill tone="problem" size="sm" label="Over budget if approved" />
          )}
        </span>
      ),
    },
    {
      id: 'budget',
      header: `Cash budget ${year}`,
      numeric: true,
      cell: (p) => (p.budget === null ? 'Not set' : formatMoney(p.budget.cashCents)),
    },
    {
      id: 'progress',
      header: 'In progress',
      numeric: true,
      cell: (p) => formatMoney(p.inProgress.cashCents),
    },
    { id: 'paid', header: 'Paid', numeric: true, cell: (p) => formatMoney(p.paid.cashCents) },
    {
      id: 'left',
      header: `Cash left ${year}`,
      numeric: true,
      // Signed: "−$2,913" when its awards would take it below zero.
      cell: (p) => (p.remaining === null ? 'Not set' : formatMoney(p.remaining.cashCents)),
    },
  ];
}

export function StockAdminPoolsCard({
  positions,
  budgetsHref,
}: {
  positions: PoolPositions;
  /** Where this reader sees the budgets and their ledgers, or null when they can't. */
  budgetsHref: string | null;
}): ReactElement {
  const year = String(positions.fiscalYear);
  const pools = poolsInOrder(positions.pools);
  return (
    <Card
      title={`Budget pools · ${year}`}
      help={{
        shows: `Each ${year} budget pool’s cash: its budget, what’s in progress, what’s paid and what’s left. A pool its awards would take below zero says “Over budget if approved”.`,
        canDo:
          'Open Budgets to see each pool’s budget, its changes and who made them. Executives change budgets and approve top-ups.',
      }}
      {...(budgetsHref !== null && {
        aside: (
          <ButtonLink href={budgetsHref} variant="ghost" size="sm">
            Budgets
          </ButtonLink>
        ),
      })}
    >
      <DataTable
        caption={`Budget pools · ${year}`}
        columns={columns(year)}
        rows={pools}
        rowKey={(p) => p.pool.id}
        minWidth={640}
        cards
        rowTone={(p) => (overIfApproved(p) ? 'problem' : null)}
        empty={{
          title: `No pools to show for ${year}.`,
          body: 'Budget pools appear here once they exist.',
          art: 'pool',
        }}
        emptyState={
          <EmptyState
            variant="not-set"
            art="pool"
            size="sm"
            headline={`No pools to show for ${year}`}
            body="Budget pools appear here once they exist. A System Admin sets up the sectors; Executives set each pool’s budget."
          />
        }
      />
      <p className={s.poolsFoot}>
        {`Scoped to fiscal year ${year} · cash only. “In progress” counts awards sent but not yet paid.`}
      </p>
    </Card>
  );
}
